import {
  BoxGeometry,
  BufferGeometry,
  CylinderGeometry,
  Float32BufferAttribute,
  Group,
  InstancedMesh,
  Matrix4,
  Mesh,
} from "three";
import { mergeGeometries } from "three/addons/utils/BufferGeometryUtils.js";

import { BUS_STOP_RENDER_CONFIG } from "../../config/busStops.ts";
import type { CityMaterialLibrary } from "../materials/cityMaterials.ts";
import type {
  RuntimeLinearFeature,
  RuntimeWorldTilePayloadV2,
} from "../../world/schema/runtimeTile.ts";
import type { TerrainTileData } from "../../world/schema/tile.ts";
import { sampleTerrainTileData } from "../../world/terrain.ts";
import {
  busStopSourcesForTile,
  type TileBusStopSource,
} from "../../world/busStops.ts";
import {
  createEvidenceBackedBusStopFurniture,
  type BusStopFurniturePlacement,
} from "./busStopFurniture.ts";

export interface BusStopBatch {
  readonly object: Group;
  readonly stopCount: number;
  readonly roadOrientedCount: number;
  readonly furnitureStopCount: number;
  readonly shelterStopCount: number;
  readonly signOnlyStopCount: number;
}

export interface BusStopBatchOptions {
  /**
   * Full detail adds readable lettering and evidence-backed furniture. Reduced
   * tile LODs retain the same official stop position as a prominent flag.
   */
  readonly lod?: 0 | 1 | 2;
}

interface RoadProjection {
  readonly tangentX: number;
  readonly tangentZ: number;
  readonly x: number;
  readonly z: number;
  readonly distanceM: number;
}

interface BusStopPlacement extends BusStopFurniturePlacement {
  readonly roadOriented: boolean;
  readonly shelterFacesAwayFromRoad: boolean;
}

const GROUND_LIFT_M = 0.012;

const BASIC_GLYPHS: Readonly<Record<string, readonly string[]>> = Object.freeze({
  " ": ["00000", "00000", "00000", "00000", "00000", "00000", "00000"],
  ".": ["00000", "00000", "00000", "00000", "00000", "01100", "01100"],
  ",": ["00000", "00000", "00000", "00000", "01100", "01100", "00100"],
  "-": ["00000", "00000", "00000", "11111", "00000", "00000", "00000"],
  "/": ["00001", "00010", "00100", "00100", "01000", "10000", "10000"],
  "+": ["00000", "00100", "00100", "11111", "00100", "00100", "00000"],
  "0": ["01110", "10001", "10011", "10101", "11001", "10001", "01110"],
  "1": ["00100", "01100", "00100", "00100", "00100", "00100", "01110"],
  "2": ["01110", "10001", "00001", "00010", "00100", "01000", "11111"],
  "3": ["11110", "00001", "00001", "01110", "00001", "00001", "11110"],
  "4": ["00010", "00110", "01010", "10010", "11111", "00010", "00010"],
  "5": ["11111", "10000", "10000", "11110", "00001", "00001", "11110"],
  "6": ["01110", "10000", "10000", "11110", "10001", "10001", "01110"],
  "7": ["11111", "00001", "00010", "00100", "01000", "01000", "01000"],
  "8": ["01110", "10001", "10001", "01110", "10001", "10001", "01110"],
  "9": ["01110", "10001", "10001", "01111", "00001", "00001", "01110"],
  A: ["01110", "10001", "10001", "11111", "10001", "10001", "10001"],
  B: ["11110", "10001", "10001", "11110", "10001", "10001", "11110"],
  C: ["01111", "10000", "10000", "10000", "10000", "10000", "01111"],
  D: ["11110", "10001", "10001", "10001", "10001", "10001", "11110"],
  E: ["11111", "10000", "10000", "11110", "10000", "10000", "11111"],
  F: ["11111", "10000", "10000", "11110", "10000", "10000", "10000"],
  G: ["01111", "10000", "10000", "10111", "10001", "10001", "01111"],
  H: ["10001", "10001", "10001", "11111", "10001", "10001", "10001"],
  I: ["01110", "00100", "00100", "00100", "00100", "00100", "01110"],
  J: ["00111", "00010", "00010", "00010", "00010", "10010", "01100"],
  K: ["10001", "10010", "10100", "11000", "10100", "10010", "10001"],
  L: ["10000", "10000", "10000", "10000", "10000", "10000", "11111"],
  M: ["10001", "11011", "10101", "10101", "10001", "10001", "10001"],
  N: ["10001", "11001", "10101", "10011", "10001", "10001", "10001"],
  O: ["01110", "10001", "10001", "10001", "10001", "10001", "01110"],
  P: ["11110", "10001", "10001", "11110", "10000", "10000", "10000"],
  Q: ["01110", "10001", "10001", "10001", "10101", "10010", "01101"],
  R: ["11110", "10001", "10001", "11110", "10100", "10010", "10001"],
  S: ["01111", "10000", "10000", "01110", "00001", "00001", "11110"],
  T: ["11111", "00100", "00100", "00100", "00100", "00100", "00100"],
  U: ["10001", "10001", "10001", "10001", "10001", "10001", "01110"],
  V: ["10001", "10001", "10001", "10001", "10001", "01010", "00100"],
  W: ["10001", "10001", "10001", "10101", "10101", "10101", "01010"],
  X: ["10001", "10001", "01010", "00100", "01010", "10001", "10001"],
  Y: ["10001", "10001", "01010", "00100", "00100", "00100", "00100"],
  Z: ["11111", "00001", "00010", "00100", "01000", "10000", "11111"],
});

const LITHUANIAN_BASE_GLYPHS = Object.freeze({
  "Ą": "A",
  "Č": "C",
  "Ę": "E",
  "Ė": "E",
  "Į": "I",
  "Š": "S",
  "Ų": "U",
  "Ū": "U",
  "Ž": "Z",
} as const);

const CARON_GLYPHS = new Set(["Č", "Š", "Ž"]);
const OGONEK_GLYPHS = new Set(["Ą", "Ę", "Į", "Ų"]);

function glyphRows(character: string): readonly string[] {
  const upper = character.toLocaleUpperCase("lt-LT");
  const lithuanian = upper as keyof typeof LITHUANIAN_BASE_GLYPHS;
  const baseCharacter = LITHUANIAN_BASE_GLYPHS[lithuanian] ?? upper;
  const base = BASIC_GLYPHS[baseCharacter] ?? BASIC_GLYPHS[" "]!;
  if (CARON_GLYPHS.has(upper)) {
    return ["10001", "01010", ...base];
  }
  if (upper === "Ė") {
    return ["00100", "00000", ...base];
  }
  if (upper === "Ū") {
    return ["11111", "00000", ...base];
  }
  if (OGONEK_GLYPHS.has(upper)) {
    return ["00000", ...base, "00010"];
  }
  return ["00000", ...base, "00000"];
}

function limitedText(value: string, maximumCharacters: number): string {
  const normalized = value.trim().replace(/\s+/g, " ").toLocaleUpperCase("lt-LT");
  if (normalized.length <= maximumCharacters) {
    return normalized;
  }
  return `${normalized.slice(0, Math.max(1, maximumCharacters - 2)).trimEnd()}..`;
}

function translated(
  geometry: BufferGeometry,
  x: number,
  y: number,
  z: number,
): BufferGeometry {
  geometry.translate(x, y, z);
  return geometry;
}

function mergeOwned(
  geometries: readonly BufferGeometry[],
  name: string,
): BufferGeometry {
  const merged = mergeGeometries([...geometries]);
  for (const geometry of geometries) {
    geometry.dispose();
  }
  if (merged === null) {
    throw new Error(`Could not merge ${name}`);
  }
  merged.name = name;
  merged.computeBoundingBox();
  merged.computeBoundingSphere();
  return merged;
}

function createStructureGeometry(): BufferGeometry {
  const config = BUS_STOP_RENDER_CONFIG;
  const crownY = config.namePlateCenterHeightM +
    config.namePlateHeightM / 2 + 0.055;
  const geometries: BufferGeometry[] = [
    translated(new CylinderGeometry(0.14, 0.18, 0.14, 10), 0, 0.07, 0),
    translated(new CylinderGeometry(0.19, 0.19, 0.035, 12), 0, 0.0175, 0),
    translated(
      new CylinderGeometry(
        config.poleRadiusM,
        config.poleRadiusM * 1.14,
        config.poleHeightM,
        8,
      ),
      0,
      config.poleHeightM / 2,
      0,
    ),
    translated(new CylinderGeometry(0.09, 0.09, 0.12, 10), 0, crownY, 0),
    translated(
      new BoxGeometry(config.namePlateWidthM + 0.1, 0.07, 0.11),
      0,
      crownY - 0.035,
      0,
    ),
    translated(
      new BoxGeometry(config.routePlateWidthM + 0.08, 0.05, 0.08),
      0,
      config.routePlateCenterHeightM - config.routePlateHeightM / 2 - 0.025,
      0,
    ),
    translated(
      new BoxGeometry(
        config.timetableWidthM + 0.055,
        config.timetableHeightM + 0.055,
        0.07,
      ),
      0,
      config.timetableCenterHeightM,
      0,
    ),
    translated(
      new BoxGeometry(config.timetableWidthM + 0.11, 0.075, 0.11),
      0,
      config.timetableCenterHeightM + config.timetableHeightM / 2 + 0.035,
      0,
    ),
    translated(
      new BoxGeometry(config.timetableWidthM + 0.07, 0.045, 0.09),
      0,
      config.timetableCenterHeightM - config.timetableHeightM / 2 - 0.023,
      0,
    ),
  ];
  for (const [x, z] of [
    [-0.105, -0.105],
    [-0.105, 0.105],
    [0.105, -0.105],
    [0.105, 0.105],
  ] as const) {
    geometries.push(translated(
      new CylinderGeometry(0.021, 0.025, 0.035, 8),
      x,
      0.04,
      z,
    ));
  }
  for (const y of [
    config.timetableCenterHeightM - config.timetableHeightM / 2 - 0.045,
    config.timetableCenterHeightM + config.timetableHeightM / 2 + 0.075,
    config.routePlateCenterHeightM + config.routePlateHeightM / 2 + 0.035,
  ]) {
    geometries.push(translated(
      new CylinderGeometry(config.poleRadiusM * 1.28, config.poleRadiusM * 1.28, 0.04, 10),
      0,
      y,
      0,
    ));
  }
  return mergeOwned(geometries, "vilnius-bus-stop:structure");
}

function createIvoryPanelGeometry(): BufferGeometry {
  const config = BUS_STOP_RENDER_CONFIG;
  const faceZ = config.flagDepthM / 2 + 0.004;
  const geometries: BufferGeometry[] = [
    translated(
      new BoxGeometry(
        config.routePlateWidthM,
        config.routePlateHeightM,
        0.008,
      ),
      0,
      config.routePlateCenterHeightM,
      faceZ,
    ),
    translated(
      new BoxGeometry(
        config.routePlateWidthM,
        config.routePlateHeightM,
        0.008,
      ),
      0,
      config.routePlateCenterHeightM,
      -faceZ,
    ),
    translated(
      new BoxGeometry(
        config.timetableWidthM,
        config.timetableHeightM,
        0.008,
      ),
      0,
      config.timetableCenterHeightM,
      0.031,
    ),
    translated(
      new BoxGeometry(
        config.timetableWidthM,
        config.timetableHeightM,
        0.008,
      ),
      0,
      config.timetableCenterHeightM,
      -0.031,
    ),
    translated(
      new BoxGeometry(0.16, config.namePlateHeightM - 0.08, 0.012),
      -config.namePlateWidthM / 2 + 0.12,
      config.namePlateCenterHeightM,
      faceZ + 0.002,
    ),
    translated(
      new BoxGeometry(0.16, config.namePlateHeightM - 0.08, 0.012),
      config.namePlateWidthM / 2 - 0.12,
      config.namePlateCenterHeightM,
      faceZ + 0.002,
    ),
    translated(
      new BoxGeometry(0.16, config.namePlateHeightM - 0.08, 0.012),
      -config.namePlateWidthM / 2 + 0.12,
      config.namePlateCenterHeightM,
      -faceZ - 0.002,
    ),
    translated(
      new BoxGeometry(0.16, config.namePlateHeightM - 0.08, 0.012),
      config.namePlateWidthM / 2 - 0.12,
      config.namePlateCenterHeightM,
      -faceZ - 0.002,
    ),
  ];
  return mergeOwned(geometries, "vilnius-bus-stop:ivory-panels");
}

function createBlueGeometry(): BufferGeometry {
  const config = BUS_STOP_RENDER_CONFIG;
  const faceZ = config.flagDepthM / 2 + 0.016;
  const geometries: BufferGeometry[] = [
    translated(
      new BoxGeometry(
        config.namePlateWidthM,
        config.namePlateHeightM,
        config.flagDepthM,
      ),
      0,
      config.namePlateCenterHeightM,
      0,
    ),
    translated(
      new BoxGeometry(0.16, 0.9, config.flagDepthM + 0.015),
      0,
      config.timetableCenterHeightM,
      0,
    ),
  ];
  for (const face of [-1, 1]) {
    const z = face * faceZ;
    geometries.push(
      translated(
        new BoxGeometry(config.timetableWidthM - 0.08, 0.085, 0.009),
        0,
        config.timetableCenterHeightM + config.timetableHeightM / 2 - 0.075,
        z,
      ),
      translated(
        new BoxGeometry(0.026, config.timetableHeightM - 0.19, 0.009),
        -config.timetableWidthM / 2 + 0.075,
        config.timetableCenterHeightM - 0.025,
        z,
      ),
    );
    for (let row = 0; row < 7; row += 1) {
      geometries.push(translated(
        new BoxGeometry(row % 3 === 0 ? 0.31 : 0.25, 0.013, 0.009),
        0.025 - (row % 2) * 0.025,
        config.timetableCenterHeightM + 0.25 - row * 0.075,
        z,
      ));
    }
  }
  return mergeOwned(geometries, "vilnius-bus-stop:name-plate");
}

function pushPixelQuad(
  positions: number[],
  indices: number[],
  placement: Readonly<BusStopPlacement>,
  left: number,
  right: number,
  bottom: number,
  top: number,
  faceZ: number,
  backFace: boolean,
): void {
  const cosine = Math.cos(placement.yaw);
  const sine = Math.sin(placement.yaw);
  const x0 = backFace ? -right : left;
  const x1 = backFace ? -left : right;
  const points = [
    [x0, bottom],
    [x1, bottom],
    [x1, top],
    [x0, top],
  ] as const;
  const start = positions.length / 3;
  for (const [localX, localY] of points) {
    positions.push(
      placement.x + cosine * localX + sine * faceZ,
      placement.y + localY,
      placement.z - sine * localX + cosine * faceZ,
    );
  }
  if (backFace) {
    indices.push(start, start + 2, start + 1, start, start + 3, start + 2);
  } else {
    indices.push(start, start + 1, start + 2, start, start + 2, start + 3);
  }
}

function appendBitmapText(
  positions: number[],
  indices: number[],
  placement: Readonly<BusStopPlacement>,
  text: string,
  centerY: number,
  maximumWidthM: number,
  maximumHeightM: number,
): void {
  const columns = Math.max(1, text.length * 6 - 1);
  const rows = 9;
  const pixelSize = Math.min(maximumWidthM / columns, maximumHeightM / rows);
  const renderedWidth = columns * pixelSize;
  const renderedHeight = rows * pixelSize;
  const originX = -renderedWidth / 2;
  const originY = centerY + renderedHeight / 2;
  const faceOffset = BUS_STOP_RENDER_CONFIG.flagDepthM / 2 + 0.009;
  for (const [characterIndex, character] of [...text].entries()) {
    const glyph = glyphRows(character);
    for (const [rowIndex, row] of glyph.entries()) {
      for (let columnIndex = 0; columnIndex < row.length; columnIndex += 1) {
        if (row[columnIndex] !== "1") {
          continue;
        }
        const left = originX + (characterIndex * 6 + columnIndex) * pixelSize;
        const right = left + pixelSize * 0.82;
        const top = originY - rowIndex * pixelSize;
        const bottom = top - pixelSize * 0.82;
        pushPixelQuad(
          positions,
          indices,
          placement,
          left,
          right,
          bottom,
          top,
          faceOffset,
          false,
        );
        pushPixelQuad(
          positions,
          indices,
          placement,
          left,
          right,
          bottom,
          top,
          -faceOffset,
          true,
        );
      }
    }
  }
}

function textGeometry(
  placements: readonly Readonly<BusStopPlacement>[],
  kind: "name" | "routes" | "direction",
): BufferGeometry {
  const config = BUS_STOP_RENDER_CONFIG;
  const positions: number[] = [];
  const indices: number[] = [];
  for (const placement of placements) {
    const text = kind === "name"
      ? limitedText(placement.source.name, config.maximumStopNameCharacters)
      : kind === "routes"
      ? limitedText(
          placement.source.routeShortNames.join(" "),
          config.maximumRouteCharacters,
        )
      : limitedText(
          placement.source.description,
          config.maximumDirectionCharacters,
        );
    if (text.length === 0) {
      continue;
    }
    appendBitmapText(
      positions,
      indices,
      placement,
      text,
      kind === "name"
        ? config.namePlateCenterHeightM + 0.015
        : kind === "routes"
        ? config.routePlateCenterHeightM + 0.065
        : config.routePlateCenterHeightM - 0.075,
      kind === "name"
        ? config.namePlateWidthM - 0.14
        : config.routePlateWidthM - 0.12,
      kind === "name"
        ? config.nameTextMaximumHeightM
        : config.routeTextMaximumHeightM,
    );
  }
  const geometry = new BufferGeometry();
  geometry.name = `vilnius-bus-stop:${kind}-text`;
  geometry.setAttribute("position", new Float32BufferAttribute(positions, 3));
  geometry.setIndex(indices);
  geometry.computeBoundingBox();
  geometry.computeBoundingSphere();
  return geometry;
}

function nearestRoadProjection(
  source: Readonly<TileBusStopSource>,
  roads: readonly Readonly<RuntimeLinearFeature>[],
): RoadProjection | null {
  let nearest: RoadProjection | null = null;
  for (const road of roads) {
    for (let index = 1; index < road.points.length; index += 1) {
      const from = road.points[index - 1];
      const to = road.points[index];
      if (from === undefined || to === undefined) {
        continue;
      }
      const deltaX = to.x - from.x;
      const deltaZ = to.z - from.z;
      const lengthSquared = deltaX * deltaX + deltaZ * deltaZ;
      if (lengthSquared <= 0.01) {
        continue;
      }
      const alpha = Math.max(0, Math.min(1,
        ((source.position.x - from.x) * deltaX +
          (source.position.z - from.z) * deltaZ) / lengthSquared,
      ));
      const x = from.x + deltaX * alpha;
      const z = from.z + deltaZ * alpha;
      const distanceM = Math.hypot(source.position.x - x, source.position.z - z);
      if (nearest === null || distanceM < nearest.distanceM) {
        const length = Math.sqrt(lengthSquared);
        nearest = {
          tangentX: deltaX / length,
          tangentZ: deltaZ / length,
          x,
          z,
          distanceM,
        };
      }
    }
  }
  return nearest;
}

function placementFor(
  source: Readonly<TileBusStopSource>,
  roads: readonly Readonly<RuntimeLinearFeature>[],
  tile: Readonly<RuntimeWorldTilePayloadV2>,
  groundingTerrain: Readonly<TerrainTileData>,
): BusStopPlacement | null {
  const terrainY = sampleTerrainTileData(
    groundingTerrain,
    { x: 0, z: 0 },
    source.position,
  );
  if (terrainY === null) {
    return null;
  }
  const road = nearestRoadProjection(source, roads);
  const roadOriented = road !== null &&
    road.distanceM <= BUS_STOP_RENDER_CONFIG.maximumOrientationRoadDistanceM;
  let yaw = roadOriented
    ? Math.atan2(-road.tangentZ, road.tangentX)
    : 0;
  let shelterFacesAwayFromRoad = false;
  if (roadOriented && road.distanceM > 0.25) {
    const awayX = source.position.x - road.x;
    const awayZ = source.position.z - road.z;
    const shelterBackX = -Math.sin(yaw);
    const shelterBackZ = -Math.cos(yaw);
    if (shelterBackX * awayX + shelterBackZ * awayZ < 0) {
      yaw += Math.PI;
    }
    shelterFacesAwayFromRoad = true;
  }
  return {
    source,
    x: source.position.x,
    y: terrainY + GROUND_LIFT_M,
    z: source.position.z,
    yaw,
    roadOriented,
    shelterFacesAwayFromRoad,
  };
}

export function createBusStopBatch(
  tile: Readonly<RuntimeWorldTilePayloadV2>,
  materials: CityMaterialLibrary,
  groundingTerrain: Readonly<TerrainTileData> = tile.terrain,
  options: Readonly<BusStopBatchOptions> = {},
): BusStopBatch | null {
  const lod = options.lod ?? 0;
  const sources = busStopSourcesForTile(tile);
  if (sources.length === 0) {
    return null;
  }
  const roads = tile.transportLines.filter((feature) => feature.kind === "road");
  const placements = sources.flatMap((source) => {
    const placement = placementFor(source, roads, tile, groundingTerrain);
    return placement === null ? [] : [placement];
  });
  if (placements.length === 0) {
    return null;
  }

  const object = new Group();
  object.name = `runtime-bus-stops:${tile.id}`;
  object.userData = {
    tileId: tile.id,
    sourceLayer: "judu-static-gtfs-stops",
    sourceStopCount: sources.length,
    renderedStopCount: placements.length,
    roadOrientedCount: placements.filter((placement) => placement.roadOriented).length,
    placement: "exact-gtfs-platform-position-terrain-grounded",
    representation: "named-vilnius-blue-white-stop-flag-with-evidence-backed-furniture",
    furnitureStopCount: placements.filter(({ source }) => source.furniture !== null).length,
    shelterStopCount: placements.filter(({ source }) => source.furniture?.shelter === true).length,
    signOnlyStopCount: placements.filter(({ source }) => source.furniture?.shelter !== true).length,
    lod,
    labelsRendered: lod === 0,
    furnitureRendered: lod === 0,
    labels: placements.map(({ source, shelterFacesAwayFromRoad }) => ({
      id: source.id,
      name: source.name,
      direction: source.description,
      modes: [...source.modes],
      routes: [...source.routeShortNames],
      furnitureProfile: source.furniture?.profile ?? "prominent-sign-only",
      shelterFacesAwayFromRoad,
    })),
  };

  const batches = [
    ["structures", createStructureGeometry(), materials.streetPole],
    ["flags", createBlueGeometry(), materials.busStopBlue],
    ["panels", createIvoryPanelGeometry(), materials.busStopIvory],
  ] as const;
  const matrix = new Matrix4();
  for (const [name, geometry, material] of batches) {
    const mesh = new InstancedMesh(geometry, material, placements.length);
    mesh.name = `${object.name}:${name}`;
    mesh.castShadow = false;
    mesh.receiveShadow = false;
    for (const [index, placement] of placements.entries()) {
      matrix.makeRotationY(placement.yaw);
      matrix.setPosition(placement.x, placement.y, placement.z);
      mesh.setMatrixAt(index, matrix);
    }
    mesh.instanceMatrix.needsUpdate = true;
    object.add(mesh);
  }

  if (lod === 0) {
    const nameText = new Mesh(
      textGeometry(placements, "name"),
      materials.busStopIvory,
    );
    nameText.name = `${object.name}:stop-names`;
    nameText.castShadow = false;
    nameText.receiveShadow = false;
    nameText.renderOrder = 13;
    object.add(nameText);

    for (const kind of ["routes", "direction"] as const) {
      const mesh = new Mesh(
        textGeometry(placements, kind),
        materials.busStopBlue,
      );
      mesh.name = `${object.name}:${kind}`;
      mesh.castShadow = false;
      mesh.receiveShadow = false;
      mesh.renderOrder = 13;
      object.add(mesh);
    }

    for (const placement of placements) {
      const furniture = createEvidenceBackedBusStopFurniture(
        placement,
        materials,
        (x, z) => sampleTerrainTileData(
          groundingTerrain,
          { x: 0, z: 0 },
          { x, z },
        ),
      );
      if (furniture !== null) {
        object.add(furniture);
      }
    }
  }

  return {
    object,
    stopCount: placements.length,
    roadOrientedCount: placements.filter((placement) => placement.roadOriented).length,
    furnitureStopCount: placements.filter(({ source }) => source.furniture !== null).length,
    shelterStopCount: placements.filter(({ source }) => source.furniture?.shelter === true).length,
    signOnlyStopCount: placements.filter(({ source }) => source.furniture?.shelter !== true).length,
  };
}
